// A designed Kokoro voice, as kokoro-axum resolves it at request time
// (`voice_recipe` on its speech request): a weighted combination of the
// English stock voices plus amounts along its style axes. Shared by the
// `voice_persona.recipe` jsonb column in `@listening/db` and the admin
// app's voice-mix contract. A leaf module for the same reason as
// `./subscription-history`: this package is browser-bundled, so it may not
// import from `@listening/db`, but `@listening/db` may import from here.
import { Schema } from 'effect';

export const VOICE_RECIPE_AXES = [
  'depth',
  'breathiness',
  'british',
  'gravel',
] as const;

export const VoiceRecipeAxes = Schema.Struct({
  depth: Schema.optional(Schema.Finite),
  breathiness: Schema.optional(Schema.Finite),
  british: Schema.optional(Schema.Finite),
  gravel: Schema.optional(Schema.Finite),
});
export type VoiceRecipeAxes = typeof VoiceRecipeAxes.Type;

export const VoiceRecipe = Schema.Struct({
  blend: Schema.Record(Schema.String, Schema.Finite),
  axes: VoiceRecipeAxes,
});
export type VoiceRecipe = typeof VoiceRecipe.Type;

// What a persona stores beyond the resolved style: the G2P voice kokoro-axum
// routes the language by (the blend's lead voice), and the playback speed,
// which is a synthesis parameter rather than part of the style table.
export const VoicePersonaSettings = Schema.Struct({
  recipe: VoiceRecipe,
  voice: Schema.String,
  speed: Schema.Finite,
});
export type VoicePersonaSettings = typeof VoicePersonaSettings.Type;

export const decodeVoicePersonaSettings =
  Schema.decodeUnknownSync(VoicePersonaSettings);
export const encodeVoicePersonaSettings =
  Schema.encodeSync(VoicePersonaSettings);

// How a persona is named wherever a voice id goes: user preferences, TTS
// requests, the TTS cache and offline archives. A published persona's settings
// never change, so the id alone keys its audio.
const PERSONA_VOICE_PREFIX = 'persona-';
const isPersonaUuid = Schema.is(Schema.String.check(Schema.isUUID()));

export const personaVoiceId = (personaId: string) =>
  `${PERSONA_VOICE_PREFIX}${personaId.toLowerCase()}`;

export const personaIdFromVoice = (voice: string) => {
  const lower = voice.toLowerCase();
  if (!lower.startsWith(PERSONA_VOICE_PREFIX)) return undefined;
  const id = lower.slice(PERSONA_VOICE_PREFIX.length);
  return isPersonaUuid(id) ? id : undefined;
};
